// ProposalsCard.tsx — "Proposals" card on the single lead page (right column).
// Lists what was sent to this lead, lets the team send a new one and mark each
// accepted / rejected.
//
// SEND PROPOSAL opens the templates sidebar; picking a template opens the send form
// with it preselected. Both roles can send and change status. Template management
// lives on /proposals (admin only, via the main sidebar).
//
// The card is self-contained: it loads its own list, and tells the page when
// something was sent (onChanged) because the server also writes a "Proposal
// sent" entry on the lead's timeline.

import React, { useCallback, useEffect, useState } from 'react';
import { Toast } from 'primereact/toast';
import { Check, ChevronDown, ChevronUp, FileText, History, Loader2, Mail, MessageCircle, Paperclip, RotateCcw, Send, X } from 'lucide-react';
import { G, C } from '../../../components/GlobalComponents/uiTokens';
import { Card, SectionHeader } from '../LeadDetails';
import SendProposalModal from './SendProposalModal';
import LeadTemplatesSidebar from './LeadTemplatesSidebar';
import ProposalsHistorySidebar from './ProposalsHistorySidebar';
import {
  Proposal,
  ProposalStatus,
  STATUS_VIEW,
  apiError,
  fmtDate,
  fmtDateTime,
  fmtINR,
  proposalApi,
  statusView,
} from '../../Proposals/parts/proposalTypes';

interface ProposalsCardProps {
  leadId: string;
  leadName: string;
  /** lead details used to fill the placeholders in the template preview */
  leadCity?: string | null;
  leadInterestedIn?: string | null;
  /** a proposal was saved — reload the lead so its timeline shows the new entry */
  onChanged?: () => void;
  toast: React.RefObject<Toast | null>;
}

const VISIBLE_DEFAULT = 3;

const smallBtn = (bg: string, color: string, border: string): React.CSSProperties => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '4px',
  padding: '3.7px 9px',
  borderRadius: '6.5px',
  border: `1px solid ${border}`,
  background: bg,
  color,
  fontSize: '0.62rem',
  fontWeight: 800,
  letterSpacing: '0.3px',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
});

const ProposalsCard: React.FC<ProposalsCardProps> = ({ leadId, leadName, leadCity, leadInterestedIn, onChanged, toast }) => {

  const [proposals, setProposals] = useState<Proposal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [allOpen, setAllOpen] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null); // row whose sent message is expanded
  const [busyId, setBusyId] = useState<string | null>(null);
  const [sendOpen, setSendOpen] = useState(false);
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [pickedTemplateId, setPickedTemplateId] = useState<string | null>(null);
  const [resendFrom, setResendFrom] = useState<Proposal | null>(null); // earlier proposal being sent again

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await proposalApi.lead.list(leadId);
      setProposals(res.data ?? []);
      setError(null);
    } catch (e) {
      setError(apiError(e, 'Failed to load proposals'));
    } finally {
      setLoading(false);
    }
  }, [leadId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSent = (p: Proposal) => {
    setProposals((cur) => [p, ...cur.filter((x) => x.id !== p.id)]);
    toast.current?.show({
      severity: 'success',
      summary: p.channel === 'email' ? 'Proposal emailed' : 'Proposal saved',
      detail: p.template_name,
      life: 3000,
    });
    onChanged?.();
  };

  const changeStatus = async (p: Proposal, status: ProposalStatus) => {
    setBusyId(p.id);
    try {
      const res = await proposalApi.lead.setStatus(p.id, status);
      setProposals((cur) => cur.map((x) => (x.id === p.id ? res.data : x)));
      toast.current?.show({ severity: 'success', summary: status === 'sent' ? 'Proposal reopened' : `Marked ${status}`, detail: p.template_name, life: 2200 });
    } catch (e) {
      toast.current?.show({ severity: 'error', summary: 'Could not update', detail: apiError(e, 'Try again'), life: 3500 });
    } finally {
      setBusyId(null);
    }
  };

  // Send the same template to this lead again: opens the send form with the template,
  // amount and valid-till of the earlier proposal. A fresh proposal is created (and shown
  // in the list / timeline); the old one stays as it was.
  const resend = (p: Proposal) => {
    setResendFrom(p);
    setPickedTemplateId(p.template_id);
    setAllOpen(false);
    setSendOpen(true);
  };

  // one proposal row — used by the card (latest few) and by the "all proposals" sidebar
  const renderRow = (p: Proposal) => {
        const view = statusView(p);
        const sv = STATUS_VIEW[view];
        const open = openId === p.id;
        const busy = busyId === p.id;
        const ChIcon = p.channel === 'email' ? Mail : MessageCircle;
        const by = p.sent_by?.email ? p.sent_by.email.split('@')[0] : null;

        return (
          <div key={p.id} style={{ padding: '9px 11px', background: G.shellBg, border: `1px solid ${G.line}`, borderLeft: `3px solid ${sv.color}`, borderRadius: '8.3px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '7px', marginBottom: '4px' }}>
              <ChIcon size={12} color={p.channel === 'email' ? C.tealMid : '#128c4a'} style={{ flexShrink: 0 }} />
              <span style={{ flex: 1, minWidth: 0, fontSize: '0.76rem', fontWeight: 700, color: C.teal, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={p.template_name}>
                {p.template_name}
              </span>
              <span style={{ padding: '1.8px 8px', borderRadius: '20px', fontSize: '0.6rem', fontWeight: 800, background: sv.bg, color: sv.color, border: `1px solid ${sv.border}`, flexShrink: 0 }}>{sv.label}</span>
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px 11px', fontSize: '0.66rem', color: C.sub, marginBottom: '7px' }}>
              {typeof p.amount === 'number' && <span style={{ fontWeight: 700, color: '#1f2937' }}>{fmtINR(p.amount)}</span>}
              {p.valid_till && <span style={{ color: view === 'expired' ? '#b45309' : C.sub, fontWeight: view === 'expired' ? 700 : 500 }}>{view === 'expired' ? 'Expired' : 'Valid till'} {fmtDate(p.valid_till)}</span>}
              <span title={fmtDateTime(p.sent_at)}>
                {by ? `${by} · ` : ''}
                {fmtDate(p.sent_at)}
              </span>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '5px', flexWrap: 'wrap' }}>
              {/* both roles may change the status */}
              {p.status === 'sent' ? (
                <>
                  <button type="button" disabled={busy} onClick={() => changeStatus(p, 'accepted')} style={{ ...smallBtn('rgba(22,163,74,0.1)', '#15803d', 'rgba(22,163,74,0.3)'), opacity: busy ? 0.6 : 1 }}>
                    <Check size={11} strokeWidth={3} /> ACCEPTED
                  </button>
                  <button type="button" disabled={busy} onClick={() => changeStatus(p, 'rejected')} style={{ ...smallBtn('rgba(239,68,68,0.08)', '#c62828', 'rgba(239,68,68,0.3)'), opacity: busy ? 0.6 : 1 }}>
                    <X size={11} /> REJECTED
                  </button>
                </>
              ) : (
                <button type="button" disabled={busy} onClick={() => changeStatus(p, 'sent')} style={{ ...smallBtn(C.white, C.sub, G.line), opacity: busy ? 0.6 : 1 }}>
                  <RotateCcw size={11} /> REOPEN
                </button>
              )}
              {busy && <Loader2 size={12} className="ldv-spin" color={C.muted} />}

              <button type="button" disabled={busy} onClick={() => resend(p)} title="Send this proposal to the lead again" style={{ ...smallBtn(C.white, C.teal, G.line), marginLeft: 'auto' }}>
                <Send size={11} /> RESEND
              </button>

              <button type="button" onClick={() => setOpenId(open ? null : p.id)} style={smallBtn('transparent', C.sub, 'transparent')}>
                {open ? <ChevronUp size={11} /> : <ChevronDown size={11} />} {open ? 'HIDE' : 'VIEW'}
              </button>
            </div>

            {/* what was actually sent — a frozen copy, unaffected by later template edits */}
            {open && (
              <div style={{ marginTop: '8px', padding: '9px 11px', background: C.white, border: `1px solid ${G.line}`, borderRadius: '7.4px' }}>
                <div style={{ fontSize: '0.64rem', color: C.muted, marginBottom: '5px' }}>
                  {p.channel === 'email' ? `Emailed to ${p.to_email ?? '—'}` : `WhatsApp to ${p.to_phone ?? '—'}`} · {fmtDateTime(p.sent_at)}
                </div>
                {p.subject && <div style={{ fontSize: '0.74rem', fontWeight: 700, color: C.teal, marginBottom: '4px' }}>{p.subject}</div>}
                <div style={{ whiteSpace: 'pre-wrap', fontSize: '0.73rem', color: '#1f2937', lineHeight: 1.55, maxHeight: '180px', overflowY: 'auto' }}>{p.body}</div>
                {p.attachments.length > 0 && (
                  <div style={{ marginTop: '7px', display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                    {p.attachments.map((a) => (
                      <a key={a.path} href={a.url} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '2px 8px', borderRadius: '20px', background: G.shellBg, border: `1px solid ${G.line}`, color: C.teal, fontSize: '0.64rem', fontWeight: 700, textDecoration: 'none' }}>
                        <Paperclip size={10} /> {a.file_name}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        );
  };

  return (
    <>
      <SendProposalModal visible={sendOpen} onHide={() => setSendOpen(false)} leadId={leadId} leadName={leadName} initialTemplateId={pickedTemplateId} initialAmount={resendFrom?.amount ?? null} initialValidTill={resendFrom?.valid_till ?? null} onSent={handleSent} />

      <LeadTemplatesSidebar
        visible={templatesOpen}
        onHide={() => setTemplatesOpen(false)}
        lead={{ name: leadName, city: leadCity, interested_in: leadInterestedIn }}
        onSelect={(t) => {
          setResendFrom(null);
          setPickedTemplateId(t.id);
          setTemplatesOpen(false);
          setSendOpen(true);
        }}
      />

      <ProposalsHistorySidebar visible={allOpen} onHide={() => setAllOpen(false)} count={proposals.length} leadName={leadName}>
        {proposals.map(renderRow)}
      </ProposalsHistorySidebar>

      <Card>
        <SectionHeader
          icon={FileText}
          title="Proposals"
          count={proposals.length}
          right={
            <button type="button" onClick={() => setTemplatesOpen(true)} style={smallBtn(C.lime, C.teal, 'transparent')}>
              <Send size={11} /> SEND PROPOSAL
            </button>
          }
        />

        {loading ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '7px', padding: '16px', color: C.muted, fontSize: '0.74rem' }}>
            <Loader2 size={14} className="ldv-spin" /> Loading…
          </div>
        ) : error ? (
          <div style={{ padding: '12px', textAlign: 'center', fontSize: '0.74rem', color: '#c62828', background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: '8.3px' }}>
            {error}
            <div style={{ marginTop: '7px' }}>
              <button type="button" onClick={load} style={smallBtn(C.white, C.teal, G.line)}>
                <RotateCcw size={11} /> TRY AGAIN
              </button>
            </div>
          </div>
        ) : proposals.length === 0 ? (
          <div style={{ padding: '16px 12px', textAlign: 'center', color: C.muted, fontSize: '0.76rem', background: G.shellBg, borderRadius: '8.3px', border: `1px dashed ${G.line}` }}>
            <FileText size={19} style={{ display: 'block', margin: '0 auto 5.5px', opacity: 0.35 }} />
            No proposal sent to {leadName.split(' ')[0] || 'this lead'} yet
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
            {proposals.slice(0, VISIBLE_DEFAULT).map(renderRow)}

            {proposals.length > VISIBLE_DEFAULT && (
              <button
                type="button"
                onClick={() => setAllOpen(true)}
                style={{ width: '100%', padding: '6px', background: 'transparent', border: `1px solid ${G.line}`, borderRadius: '7.4px', color: C.teal, fontSize: '0.69rem', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}
              >
                <History size={11} /> View all {proposals.length} proposals
              </button>
            )}
          </div>
        )}
      </Card>
    </>
  );
};

export default ProposalsCard;